import { useCallback, useEffect, useRef } from "react";
import { useWebsocket } from "@finitless/shared/contexts";
import { useUserStore, useApiStore } from "@finitless/shared/stores";

/**
 * Hook for joining specific conversation rooms
 * Allows components to listen to real-time updates for specific conversations
 */
export const useConversationRoom = (conversationId, agentExternalId = null) => {
  const { socket, isConnected } = useWebsocket();
  const user = useUserStore((state) => state.user);
  const currentProject = useApiStore((state) => state.project);
  const joinedRoomsRef = useRef(new Set());

  /**
   * Join a room directly using the socket
   */
  const joinRoomDirect = useCallback(
    (roomConfig) => {
      if (socket?.socket?.connected) {
        socket.socket.emit("join", roomConfig);
      }
    },
    [socket],
  );

  /**
   * Leave a room directly using the socket
   */
  const leaveRoomDirect = useCallback(
    (roomName) => {
      if (socket?.socket?.connected) {
        socket.socket.emit("leave", roomName);
      }
    },
    [socket],
  );

  /**
   * Join a specific conversation room
   */
  const joinConversationRoom = useCallback(
    (roomType = "conversation") => {
      if (
        !socket ||
        !isConnected ||
        !conversationId ||
        !user?.id ||
        !currentProject
      ) {
        return;
      }

      // Determine role based on user level
      const role = user.level === 0 ? "manager" : "customer";

      let roomName;
      let roomConfig;

      switch (roomType) {
        case "conversation":
          roomName = `conversations:${conversationId}`;
          roomConfig = {
            room: roomName,
            project: currentProject,
            user_id: user.id,
            role: role,
          };
          break;

        case "messages":
          roomName = `conversations:${conversationId}:messages`;
          roomConfig = {
            room: roomName,
            project: currentProject,
            user_id: user.id,
            role: role,
          };
          break;

        case "current":
          if (!agentExternalId) {
            console.warn(
              "agentExternalId is required for current conversation room",
            );
            return;
          }
          roomName = `conversations:${agentExternalId}:current`;
          roomConfig = {
            room: roomName,
            project: currentProject,
            user_id: user.id,
            role: "customer",
          };
          break;

        case "current_messages":
          if (!agentExternalId) {
            console.warn(
              "agentExternalId is required for current conversation messages room",
            );
            return;
          }
          roomName = `conversations:${agentExternalId}:current:messages`;
          roomConfig = {
            room: roomName,
            project: currentProject,
            user_id: user.id,
            role: "customer",
          };
          break;

        default:
          console.warn("Unknown room type:", roomType);
          return;
      }

      if (!joinedRoomsRef.current.has(roomName)) {
        joinRoomDirect(roomConfig);
        joinedRoomsRef.current.add(roomName);
      }
    },
    [
      socket,
      isConnected,
      conversationId,
      agentExternalId,
      user,
      currentProject,
      joinRoomDirect,
    ],
  );

  /**
   * Leave a specific conversation room
   */
  const leaveConversationRoom = useCallback(
    (roomType = "conversation") => {
      if (!socket || !conversationId) return;

      let roomName;

      switch (roomType) {
        case "conversation":
          roomName = `conversations:${conversationId}`;
          break;

        case "messages":
          roomName = `conversations:${conversationId}:messages`;
          break;

        case "current":
          if (!agentExternalId) return;
          roomName = `conversations:${agentExternalId}:current`;
          break;

        case "current_messages":
          if (!agentExternalId) return;
          roomName = `conversations:${agentExternalId}:current:messages`;
          break;

        default:
          return;
      }

      if (joinedRoomsRef.current.has(roomName)) {
        leaveRoomDirect(roomName);
        joinedRoomsRef.current.delete(roomName);
      }
    },
    [socket, conversationId, agentExternalId, leaveRoomDirect],
  );

  /**
   * Join multiple conversation rooms at once
   */
  const joinMultipleRooms = useCallback(
    (roomTypes = ["conversation", "messages"]) => {
      roomTypes.forEach((roomType) => {
        joinConversationRoom(roomType);
      });
    },
    [joinConversationRoom],
  );

  /**
   * Leave all conversation rooms
   */
  const leaveAllRooms = useCallback(() => {
    const roomsToLeave = Array.from(joinedRoomsRef.current);
    roomsToLeave.forEach((roomName) => {
      leaveRoomDirect(roomName);
    });
    joinedRoomsRef.current.clear();
  }, [leaveRoomDirect]);

  // Auto-join conversation room when conversationId changes
  useEffect(() => {
    if (conversationId && isConnected && user?.id && currentProject) {
      // Only join if we haven't already joined these rooms
      const conversationRoom = `conversations:${conversationId}`;
      const messagesRoom = `conversations:${conversationId}:messages`;
      if (!joinedRoomsRef.current.has(conversationRoom)) {
        joinConversationRoom("conversation");
      }
      if (!joinedRoomsRef.current.has(messagesRoom)) {
        joinConversationRoom("messages");
      }
      // This effect owns only these two rooms. On ID/disconnect changes,
      // release the previous pair before joining the new conversation.
      return () => {
        for (const room of [conversationRoom, messagesRoom]) {
          if (joinedRoomsRef.current.has(room)) {
            leaveRoomDirect(room);
            joinedRoomsRef.current.delete(room);
          }
        }
      };
    }
  }, [
    conversationId,
    isConnected,
    user?.id,
    currentProject,
    joinConversationRoom,
    leaveRoomDirect,
  ]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      leaveAllRooms();
    };
  }, [leaveAllRooms]);

  return {
    isConnected,
    joinedRooms: Array.from(joinedRoomsRef.current),
    joinConversationRoom,
    leaveConversationRoom,
    joinMultipleRooms,
    leaveAllRooms,
  };
};

export default useConversationRoom;
